import { useEffect, useMemo, useState } from "react";

import {
  ArrowButton,
  TextField,
  Checkbox,
  Select,
  Button,
  TableMetadata,
  useTable,
  Label,
} from "@king-ict/eupisi-ui/components";
import { PagedResult } from "@king-ict/eupisi-ui/models";
import { ExportTypeEnum } from "@king-ict/eupisi-ui/models/Export/ExportTypeEnum";
import {
  codeBookToSelectData,
  hrDate,
  nameof,
} from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { RequestCountyFilterSelect } from "./components";
import { REQUEST_COUNTY_FILTER_ACCESS } from "./constants";
import {
  createInitialInstitutionIds,
  createInitialRequestDataSearch,
  getInitialTableState,
  sanitizeRequestSearchApiPayload,
} from "./utils";
import {
  searchRequestData,
  getRequestDataExport,
  confirmRequest,
  generateRequest,
} from "../../api/Request/RequestApi";
import {
  ContentArea,
  FormCard,
  InstitutionSelect,
  ListTable,
  SearchCard,
  ToolbarTop,
} from "../../components";
import { ConfirmModal } from "../../components/FormControls/ConfirmModal";
import {
  useInput,
  useFilterPersistence,
  useAuth,
  useEnumCodeBooks,
  useTableStatePersistence,
} from "../../hooks";
import { ValidationFailure } from "../../models";
import {
  RequestData,
  RequestDataSearch,
  RequestSort,
  RequestStatus,
  RequestList,
} from "../../models/Request";
import { ClaimType } from "../../models/User";
import {
  useGetSchoolYearCurrentQuery,
  useGetSchoolYearListQuery,
} from "../../queries/codeBookQuery";
import { registrationFormEditUrl } from "../../routes/routes";
import { FormatUtils, TableUtils } from "../../utils";

const STORAGE_KEY = "EnrollmentRegistrationFormListSearch";

export function RegistrationFormList() {
  const { t } = useTranslation();
  const history = useHistory();
  const { handleChange } = useInput();
  const auth = useAuth();
  const {
    requestTypeCodeBook,
    requestStatusCodeBook,
    registrationFormStatusCodeBook,
  } = useEnumCodeBooks();

  const { data: schoolYear } = useGetSchoolYearCurrentQuery();

  const INITIAL_TABLE_STATE = useMemo(
    () => getInitialTableState(STORAGE_KEY),
    []
  );

  const INITIAL_INSTITUTION_IDS = useMemo(
    () => createInitialInstitutionIds(auth.currentRole),
    [auth.currentRole]
  );

  const INITIAL_REQUEST_DATA_SEARCH = useMemo(
    () => ({
      ...createInitialRequestDataSearch({
        schoolYearId: schoolYear?.id,
        initialTableState: INITIAL_TABLE_STATE,
        defaultInstitutionIds: INITIAL_INSTITUTION_IDS,
      }),
      onlyEnrollmentApproved: true,
    }),
    [schoolYear?.id, INITIAL_TABLE_STATE, INITIAL_INSTITUTION_IDS]
  );

  //TABLE DATA
  const defaultTableData = useMemo(
    () => TableUtils.tableDefaultData<RequestData>(),
    []
  );
  const [tableData, setTableData] =
    useState<PagedResult<RequestData>>(defaultTableData);

  //SET SEARCH FILTERS
  const {
    filterState: requestDataSearch,
    setFilterState: setRequestDataSearch,
    saveFilters,
    clearFilters,
    filterChanged,
    setClearFilter,
  } = useFilterPersistence<RequestDataSearch>({
    storageKey: STORAGE_KEY,
    initialState: INITIAL_REQUEST_DATA_SEARCH,
  });

  //TABLE METADATA
  const tableMetadata: TableMetadata<RequestData> = {
    rowKey: "id",
    columns: [
      {
        columnKey: "select",
        header: "",
        accessor: (item: RequestData) => {
          return (
            <div>
              <Checkbox
                label={""}
                name={nameof<typeof item>("id")}
                value={item.id}
                checked={item.isSelected}
                onChange={handleItemCheckedChange}
              />
            </div>
          );
        },
      },
      {
        columnKey: "code",
        header: t("common.schoolYearModel.code"),
        accessor: (item: RequestData) => {
          return <div className={"text-nowrap text-right"}>{item.code}</div>;
        },
        sortBy: RequestSort.Code,
      },
      {
        columnKey: "firstName",
        header:
          t("common.schoolCandidateModel.lastName") +
          "/" +
          t("common.schoolCandidateModel.firstName"),
        accessor: (item: RequestData) => {
          return (
            <div>
              <div className={"text-nowrap"}>
                {item.lastName},&nbsp;{item.firstName}
              </div>
              <div className={"text-nowrap"}>
                {t("common.schoolCandidateModel.oib")}:&nbsp;{item.childOib}
              </div>
            </div>
          );
        },
        sortBy: RequestSort.LastName,
      },
      {
        columnKey: "dateOfBirth",
        header: t("common.schoolCandidateModel.dateOfBirth"),
        accessor: (item: RequestData) => {
          return (
            <div className={"text-nowrap text-right"}>
              {item.dateOfBirth ? hrDate(new Date(item.dateOfBirth)) : ""}
            </div>
          );
        },
        sortBy: RequestSort.Birthday,
      },
      {
        columnKey: "permanentAddress",
        header:
          t("common.schoolCandidateModel.permanentAddress") +
          "/" +
          t("common.schoolCandidateModel.temporaryAddress"),
        accessor: (item: RequestData) => {
          return (
            <>
              <div className={"text-nowrap"}>
                <b>
                  {item.permanentAddress?.street}&nbsp;
                  {item.permanentAddress?.houseNumber},&nbsp;
                  {item.permanentAddress?.town}{" "}
                </b>
              </div>
              {item.temporaryAddress && (
                <div className={"text-nowrap"}>
                  {item.temporaryAddress.street}&nbsp;
                  {item.temporaryAddress.houseNumber},&nbsp;
                  {item.temporaryAddress.town}{" "}
                </div>
              )}
            </>
          );
        },
        sortBy: RequestSort.Address,
      },
      {
        columnKey: "type",
        header: t("common.requestModel.requestType"),
        accessor: (item: RequestData) => {
          return (
            <Label
              color={FormatUtils.getRequestTypeColor(item.requestType)}
              className="text-center"
              outline={false}
              fullWidth={false}
            >
              {requestTypeCodeBook.find((v) => v.id === item.requestType)?.name}
            </Label>
          );
        },
        sortBy: RequestSort.Type,
      },
      {
        columnKey: "status",
        header: t("common.requestModel.requestStatus"),
        accessor: (item: RequestData) => {
          return (
            <Label
              color={FormatUtils.getRequestStatusColor(item.status)}
              className="text-center"
              outline={false}
              fullWidth={false}
            >
              {requestStatusCodeBook.find((v) => v.id === item.status)?.name}
            </Label>
          );
        },
        sortBy: RequestSort.Status,
      },
      {
        columnKey: "confirmed",
        header: t("common.requestModel.confirmedEnrollment"),
        accessor: (item: RequestData) => {
          return (
            <>
              {item.status === RequestStatus.EnrollmentConfirmed ||
              item.status === RequestStatus.RegistrationFormCreated ||
              item.status === RequestStatus.Registered
                ? t("common.yes")
                : t("common.no")}
            </>
          );
        },
      },
      {
        columnKey: "registrationFormStatus",
        header: t("common.requestModel.registrationFormStatus"),
        accessor: (item: RequestData) => {
          return (
            <>
              {item.registrationFormStatus?.adminName ??
                registrationFormStatusCodeBook.find(
                  (v) => v.id === item.registrationFormStatus?.id
                )?.name ??
                t("registrationForm.notCreated")}
            </>
          );
        },
      },
      {
        columnKey: "details",
        header: "",
        accessor: (item: RequestData) => {
          if (
            item.status === RequestStatus.RegistrationFormCreated ||
            item.status === RequestStatus.Registered
          )
            return (
              <div className={"text-right"}>
                <ArrowButton
                  ariaLabel={t("common.details")}
                  onClick={() => handleDetailsClicked(item.id, registrationFormEditUrl)}
                />
              </div>
            );
          else return <></>;
        },
      },
    ],
    rowToolTip: () => undefined,
  };
  //SET TABLE
  const [tableProps, tableState, setFilters] = useTable<RequestDataSearch>({
    metadata: tableMetadata,
    data: tableData!.results,
    pagination: true,
    paginationPage: INITIAL_TABLE_STATE.page,
    paginationPageSize: INITIAL_TABLE_STATE.pageSize as any,
    totalItems: tableData!.rowCount,
    defaultSort: {
      order: INITIAL_TABLE_STATE.order,
      sortBy: INITIAL_TABLE_STATE.sortBy,
    },
  });
  const { data: schoolYears } = useGetSchoolYearListQuery();

  const { handleDetailsClicked } = useTableStatePersistence({
    storageKey: STORAGE_KEY,
    requestDataSearch,
    tableState,
  });

  //EDIT DATA
  const createDefaultRequestList = (): RequestList => {
    return {
      idList: [],
    };
  };
  const [selectAll, setSelectAll] = useState(false);
  const [requestList, setRequestList] = useState<RequestList>(
    createDefaultRequestList
  );
  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [confirmState, setConfirmState] = useState<any>();
  const [confirmStateGenerate, setConfirmStateGenerate] = useState<any>();

  const canApplyCountyFilter = useMemo(
    () => auth.hasRoleAccess(REQUEST_COUNTY_FILTER_ACCESS),
    [auth]
  );

  useEffect(() => {
    if (!requestDataSearch.schoolYearId && schoolYear && schoolYear.id) {
      setRequestDataSearch({
        ...requestDataSearch,
        schoolYearId: schoolYear.id,
      });
    }
  }, [schoolYear, requestDataSearch.schoolYearId]);

  useEffect(() => {
    (async () => {
      const request = TableUtils.GetSearchRequest<RequestDataSearch>(
        requestDataSearch,
        tableState
      );
      const itemList = await searchRequestData(
        sanitizeRequestSearchApiPayload(request, canApplyCountyFilter)
      );
      if (itemList !== null && itemList.results.length > 0) {
        setTableData(itemList);
      } else {
        setTableData(defaultTableData);
      }
      clearAssignmentButtons();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState, canApplyCountyFilter]);

  const handleInstitutionChange = (id: number) => {
    const newSearch: RequestDataSearch = {
      ...requestDataSearch,
      institutionIds: !isNaN(id) ? [id] : [],
    };
    setRequestDataSearch(newSearch);
  };

  const handleSearchClicked = () => {
    doSearch();
  };
  const handleClearSearchClicked = () => {
    const newRequestDataSearch: RequestDataSearch = {
      schoolYearId: schoolYear?.id,
      institutionIds: INITIAL_INSTITUTION_IDS,
      onlyRequests: false,
      onlyWithAddReasonDelayPreviousYear: false,
      onlyNotSchoolCandidate: false,
      onlyOutsideEnrollmentArea: false,
      onlyLanguageCourseRequired: false,
      onlyMinistryLanguageCourseRequired: false,
      onlyEnrollmentApproved: true,
      onlyEnrolled: false,
      onlyOutsideEnrollmentAreaOnlyEditable: false,
      outsideEnrollmentAreaStatus:
        INITIAL_TABLE_STATE.outsideEnrollmentAreaStatus,
    };

    setClearFilter(true);
    setRequestDataSearch(newRequestDataSearch);
    clearFilters();
    doSearch();
  };
  const handleDownloadClicked = async (exportType: ExportTypeEnum) => {
    await getRequestDataExport(
      sanitizeRequestSearchApiPayload(
        requestDataSearch,
        canApplyCountyFilter
      ),
      exportType
    );
  };
  const doSearch = () => {
    setFilters({ ...requestDataSearch });
  };
  //EDIT HANDLERS
  const clearAssignmentButtons = () => {
    setSelectAll(false);
    setRequestList(createDefaultRequestList());
  };
  const handleItemCheckedChange = (e: any) => {
    const itemId = Number.parseInt(e.target.value);
    const isChecked = e.target.checked as boolean;
    if (itemId) {
      const request = tableData.results.find((val) => val.id === itemId);
      let list = requestList.idList;
      if (isChecked && request) {
        list.push(itemId);
        request!.isSelected = true;
      } else {
        list = list.filter((val) => val !== itemId);
        setSelectAll(false);
        request!.isSelected = false;
      }
      const assignmentList: RequestList = {
        ...requestList,
        idList: list,
      };
      setRequestList(assignmentList);
    }
  };
  const handleSelectAllCheckedChange = (e: any) => {
    const isChecked = !selectAll;
    setSelectAll(isChecked);
    const newResults = tableData.results.map((val) => {
      val.isSelected = isChecked;
      return val;
    });
    const newTableData: PagedResult<RequestData> = {
      ...tableData,
      results: newResults,
    };
    const newUpdate: RequestList = {
      ...requestList,
      idList: newResults.filter((val) => val.isSelected).map((val) => val.id),
    };
    setTableData(newTableData);
    setRequestList(newUpdate);
  };
  const handleConfirmClicked = async () => {
    const result = await confirmRequest(requestList);
    if (result) {
      if (result.isValid) {
        setConfirmState("message");
      } else {
        setErrors(result.validationFailures);
      }
      doSearch();
    } else {
      setConfirmState("error");
    }
  };
  const handleGenerateClicked = async () => {
    const result = await generateRequest(requestList);
    if (result) {
      if (result.isValid) {
        setConfirmStateGenerate("message");
      } else {
        setErrors(result.validationFailures);
      }
      doSearch();
    } else {
      setConfirmStateGenerate("error");
    }
  };
  const getConfirmEnabled = (): boolean => {
    const requestDataList = tableData.results.filter((val) => val.isSelected);
    const result =
      requestDataList.length > 0 &&
      requestDataList.find(
        (val) =>
          val.status !== RequestStatus.EnrollmentApproved &&
          val.status !== RequestStatus.ProcedureApproved &&
          val.status !== RequestStatus.DecisionOnAppropriateProgram
      ) === undefined;
    return result;
  };
  const getGenerationEnabled = (): boolean => {
    const requestDataList = tableData.results.filter((val) => val.isSelected);
    const result =
      requestDataList.length > 0 &&
      requestDataList.find(
        (val) => val.status !== RequestStatus.EnrollmentConfirmed
      ) === undefined;
    return result;
  };
  return (
    <ContentArea>
      <ToolbarTop
        pageTitle={t("common.requestModel.pageName")}
        showAdd={"hide"}
        errors={errors}
      />
      <SearchCard
        onClickSearch={handleSearchClicked}
        onClickClearSearch={handleClearSearchClicked}
        isFilterChanged={filterChanged}
        cardTitle={t("common.requestModel.searchCardName")}
        hideDivider={true}
      >
        <Row>
          <Col md={12}>
            <Select
              label={t("common.schoolCandidateModel.schoolYearId")}
              name={nameof<typeof requestDataSearch>("schoolYearId")}
              value={requestDataSearch.schoolYearId}
              required={true}
              onChange={handleChange(setRequestDataSearch)}
              selectData={codeBookToSelectData(schoolYears)}
            />
          </Col>
        </Row>
        {canApplyCountyFilter && (
          <Row>
            <Col md={6}>
              <RequestCountyFilterSelect
                value={requestDataSearch.countyId}
                setFilterState={setRequestDataSearch}
              />
            </Col>
          </Row>
        )}
        <Row>
          <Col md={8}>
            <InstitutionSelect
              label={t("common.schoolCandidateModel.institutionList")}
              name={nameof<typeof requestDataSearch>("institutionIds")}
              value={
                requestDataSearch.institutionIds &&
                requestDataSearch.institutionIds.length > 0
                  ? requestDataSearch.institutionIds[0]
                  : undefined
              }
              countyIds={
                auth.currentRole?.claimType === ClaimType.County
                  ? auth.currentRole.locations
                  : undefined
              }
              ids={
                auth.currentRole?.claimType === ClaimType.Institution
                  ? auth.currentRole.locations
                  : undefined
              }
              onlyByEnrollmentArea={false}
              excludeHandicapped={false}
              excludePrivate={false}
              excludeSatellite={false}
              onChange={(e) =>
                handleInstitutionChange(Number.parseInt(e.target.value))
              }
              showUser={true}
              shouldCheckParentCounty
            />
          </Col>
          <Col md={3}>
            <TextField
              label={t("common.schoolYearModel.code")}
              name={nameof<typeof requestDataSearch>("code")}
              value={requestDataSearch.code}
              onChange={handleChange(setRequestDataSearch)}
            />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.lastName")}
              name={nameof<typeof requestDataSearch>("lastName")}
              value={requestDataSearch.lastName}
              onChange={handleChange(setRequestDataSearch)}
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.firstName")}
              name={nameof<typeof requestDataSearch>("firstName")}
              value={requestDataSearch.firstName}
              onChange={handleChange(setRequestDataSearch)}
            />
          </Col>
          <Col md={3}>
            <TextField
              label={t("common.schoolCandidateModel.oib")}
              name={nameof<typeof requestDataSearch>("oib")}
              value={requestDataSearch.oib}
              onChange={handleChange(setRequestDataSearch)}
            />
          </Col>
        </Row>
        <Row>
          <Col md={3}>
            <Select
              label={t("common.requestModel.requestType")}
              name={nameof<typeof requestDataSearch>("requestType")}
              value={requestDataSearch.requestType}
              onChange={handleChange(setRequestDataSearch)}
              selectData={codeBookToSelectData(requestTypeCodeBook)}
              emptyOption={{ text: t("common.all"), value: undefined }}
            />
          </Col>
          <Col md={3}>
            <Select
              label={t("common.requestModel.requestStatus")}
              name={nameof<typeof requestDataSearch>("requestStatus")}
              value={requestDataSearch.requestStatus}
              onChange={handleChange(setRequestDataSearch)}
              selectData={codeBookToSelectData(requestStatusCodeBook)}
              emptyOption={{ text: t("common.all"), value: undefined }}
            />
          </Col>
        </Row>
      </SearchCard>
      <FormCard>
        <Row className="m-1 pb-2 pt-2 bg-light border">
          <Col md={2} className="pt-3">
            <Checkbox
              label={t("common.selectAll")}
              name={"selectAll"}
              checked={selectAll}
              onChange={handleSelectAllCheckedChange}
            />
          </Col>
          <Col md={3} className="pt-3">
            <Button
              onClick={() => setConfirmState("confirm")}
              outline={true}
              size="sm"
              disabled={!getConfirmEnabled()}
            >
              {t("common.requestModel.enrollmentConfirm")}
            </Button>
          </Col>
          <Col md={3} className="pt-3">
            <Button
              onClick={() => setConfirmStateGenerate("confirm")}
              size="sm"
              disabled={!getGenerationEnabled()}
            >
              {t("common.requestModel.registrationGenerate")}
            </Button>
          </Col>
        </Row>
      </FormCard>
      <ListTable
        tableProps={tableProps}
        onClickDownload={(exportType: ExportTypeEnum) =>
          handleDownloadClicked(exportType)
        }
        showDownload
      />
      <ConfirmModal
        title={t("common.requestModel.enrollmentConfirm")}
        confirmState={confirmState}
        confirmMessage={t("common.requestModel.confirmMessage")}
        successMesage={t("common.requestModel.successMesage")}
        errorMessage={t("common.requestModel.errorMessage")}
        onClickClose={() => setConfirmState(undefined)}
        onClickConfirm={handleConfirmClicked}
      />
      <ConfirmModal
        title={t("common.requestModel.registrationGenerate")}
        confirmState={confirmStateGenerate}
        confirmMessage={t("common.requestModel.confirmMessageGenerate")}
        confirmDescription={t("common.requestModel.confirmDescriptionGenerate")}
        successMesage={t("common.requestModel.successMesageGenerate")}
        errorMessage={t("common.requestModel.errorMessageGenerate")}
        onClickClose={() => setConfirmStateGenerate(undefined)}
        onClickConfirm={handleGenerateClicked}
      />
    </ContentArea>
  );
}
